import styled from "../_snowpack/pkg/@emotion/styled.js";
import React, {useRef, useState} from "../_snowpack/pkg/react.js";
import {t} from "../common/i18n.js";
import {getUrl} from "../common/universal.js";
import {Routes} from "../typings/routes.js";
import Loader from "./loader.js";
import Stack from "./stack.js";
import {colors, fontFamily, gap, media} from "./theme.js";
import {printSurfaceLabel} from "../common/translations.js";
import {
  callIframeMethod,
  getPartsFromPrintRequirements
} from "../common/utils.js";
import Icon from "./icon.js";
import {useIframeListener} from "../common/hooks.js";
export default function ErpaEditor(props) {
  const {
    editorConfig,
    outsideTexturePath,
    insideTexturePath,
    productSlug,
    article,
    alternative_erpa_standard: alternativeStandard,
    extra_erpa_parameters = "",
    ...rest
  } = props;
  const {printRequirements} = editorConfig;
  const standard = alternativeStandard ? alternativeStandard : editorConfig.standard;
  const iframeSrc = getUrl(Routes.editorGetFrame, {}, {
    query: {
      editorConfig: JSON.stringify(editorConfig),
      standard,
      outsideTexturePath,
      insideTexturePath,
      productSlug,
      articleId: article.id
    }
  }) + `&${extra_erpa_parameters}`;
  const parts = getPartsFromPrintRequirements(printRequirements);
  const hasParts = parts && parts.length > 0;
  const allPartsActive = (parts?.length || 0) + 1;
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [activeParts, setActiveParts] = useState(allPartsActive);
  const [playing, setPlaying] = useState(true);
  useIframeListener(() => {
    if (hasParts) {
      async () => {
        await setPlay(false);
        await setAnimationDuration(0.5);
      };
    }
    setLoading(false);
  }, iframeSrc);
  const onOpenClick = () => callIframeMethod(iframeRef, "foldOpen");
  const onCloseClick = () => callIframeMethod(iframeRef, "foldClose");
  const onIframeLoad = () => {
    setLoading(false);
  };
  const onError = () => {
    setLoading(false);
  };
  const togglePart = async (index) => {
    const aParts = activeParts ^ index;
    await callIframeMethod(iframeRef, "switchChild", aParts - 1);
    await setPlay(false);
    setActiveParts(aParts);
  };
  const togglePlay = async () => {
    await setPlay(!playing);
  };
  const setPlay = async (p) => {
    await callIframeMethod(iframeRef, p ? "tl_play" : "tl_pause");
    setPlaying(p);
  };
  const setAnimationDuration = async (duration) => {
    await callIframeMethod(iframeRef, "setAnimationDuration", duration);
  };
  const isActive = (index) => {
    return (activeParts & index) !== 0;
  };
  const showPartsButtons = !loading && hasParts;
  const showButtons = !loading && !showPartsButtons;
  return /* @__PURE__ */ React.createElement(Container, {
    ...rest
  }, /* @__PURE__ */ React.createElement(Iframe, {
    src: iframeSrc,
    ref: iframeRef,
    width: "100%",
    height: "100%",
    onLoad: onIframeLoad,
    onError,
    isLoading: loading,
    loading: "lazy"
  }), loading && /* @__PURE__ */ React.createElement(LoaderContainer, null, /* @__PURE__ */ React.createElement(Loader, null)), showButtons && /* @__PURE__ */ React.createElement(Buttons, null, /* @__PURE__ */ React.createElement(Button, {
    onClick: onOpenClick
  }, t("Aufklappen")), /* @__PURE__ */ React.createElement(Button, {
    onClick: onCloseClick
  }, t("Zuklappen"))), showPartsButtons && /* @__PURE__ */ React.createElement(Buttons, null, parts.map((p, i) => /* @__PURE__ */ React.createElement(PartButton, {
    key: p,
    onClick: () => togglePart(i + 1),
    active: isActive(i + 1)
  }, printSurfaceLabel(p)))), showPartsButtons && activeParts === allPartsActive && /* @__PURE__ */ React.createElement(PlayButton, {
    onClick: togglePlay
  }, playing ? /* @__PURE__ */ React.createElement(Icon, {
    name: "pause"
  }) : /* @__PURE__ */ React.createElement(Icon, {
    name: "play"
  })));
}
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 40px;
  overflow: hidden;

  ${({backgroundColor}) => backgroundColor && `background: ${backgroundColor}70;`}
`;
const Iframe = styled.iframe`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 0;
  visibility: visible;
  will-change: visibility;

  ${({isLoading}) => isLoading && "visibility: hidden;"}
`;
const Buttons = styled(Stack)`
  position: absolute;
  left: 40px;
  bottom: 40px;

  ${media.mobile} {
    flex-direction: row;
    ${gap("var(--spacing)", "row")}
    left: 50%;
    transform: translateX(-50%);
    bottom: 70px;
  }
`;
const Button = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px 24px;
  min-width: 127px;
  height: 32px;
  background: white;
  border-radius: 8px;
  font-family: ${fontFamily.primary};
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  cursor: pointer;
`;
const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 25px;
  transform: translate3d(0, 0, 0);
`;
const PartButton = styled(Button)`
  ${({active}) => active && `
      background: ${colors.c};
      color: white;
    `}
`;
const PlayButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 40px;
  width: 40px;
  height: 40px;
  border-radius: 100%;
  background: white;
  cursor: pointer;

  ${media.mobile} {
    bottom: auto;
    left: 20px;
    top: 70px;
    transform: none;
  }
`;
